/**
 * 本文件是菜单工具函数模块
 * @author 笑三少
 */
import {
  MenuConfig,
  CommonMenuConfig,
} from '../config/menus'
import type {
  MenuItem,
  FlatMenuItem,
  BreadcrumbItem
} from '../api'
const VALID_ROLES: UserRole[] = ['SYSTEM_ADMIN', 'ADMIN', 'TEACHER','REVIEWER']

/**
 * 获取角色对应的菜单配置
 * @param 用户角色
 */
export const getMenusByRole = (role: string): MenuItem[] => {
  try {
    // 合并通用菜单
    const mergedMenus = mergeMenus(MenuConfig, [...CommonMenuConfig])
    return sortMenus(mergedMenus)
  } catch (error) {
    return [...CommonMenuConfig]
  }
}

/**
 * 合并菜单
 * @param roleMenus 对应的角色菜单
 * @param commonMenus 公共菜单
 */
const mergeMenus = (roleMenus: MenuItem[], commonMenus: MenuItem[]): MenuItem[] => {
  const menuMap = new Map<string, MenuItem>()
  commonMenus.forEach(menu => {
    menuMap.set(menu.path, { ...menu })
  })
  roleMenus.forEach(menu => {
    if (menuMap.has(menu.path)) {
      const existing = menuMap.get(menu.path)!
      menuMap.set(menu.path, {
        ...existing,
        ...menu,
        children: mergeMenus(existing.children || [], menu.children || [])
      })
    } else menuMap.set(menu.path, { ...menu })
  })
  return Array.from(menuMap.values())
}

/**
 * 菜单排序(根据菜单列表的sort项目)
 * @param menus 菜单项目
 */
export const sortMenus = (menus: MenuItem[]): MenuItem[] => {
  return menus
    .sort((a, b) => (a.meta?.sort || 99) - (b.meta?.sort || 99))
    .map(menu => {
      if (menu.children && menu.children.length > 0) menu.children = sortMenus(menu.children)
      return menu
    })
}

/**
 * 过滤隐藏的菜单
 * @param menus 菜单项
 */
export const filterHiddenMenus = (menus: MenuItem[]): MenuItem[] => {
  return menus
    .filter(menu => !menu.meta?.hidden)
    .map(menu => {
      const filteredMenu = { ...menu }
      if (filteredMenu.children && filteredMenu.children.length) {
        filteredMenu.children = filterHiddenMenus(filteredMenu.children)
      }
      return filteredMenu
    })
    .filter(menu => !menu.children || menu.children.length > 0)
}

/**
 * 过滤有权限的菜单
 * @param menus 菜单项
 * @param userRoles 用户角色
 */
export const filterAuthorizedMenus = (
  menus: MenuItem[],
  userRoles: string[]
): MenuItem[] => {
  // 性能优化：提前创建角色Set
  const userRoleSet = new Set(userRoles)
  return menus
    .filter(menu => hasPermission(menu, userRoleSet))
    .map(menu => ({
      ...menu,
      children: menu.children?.length
        ? filterAuthorizedMenus(menu.children, userRoles)
        : undefined
    }))
}

/**
 * 检查用户是否有菜单权限
 * @param menu 菜单
 * @param userRoles 用户校色(admin|user|guest)
 */
const hasPermission = (
  menu: MenuItem,
  userRoleSet: Set<string>
): boolean => {
  // 没有设置roles表示所有用户可访问
  if (!menu.roles || menu.roles.length === 0) return true
  // 检查是否有交集
  return menu.roles.some(role => userRoleSet.has(role))
}

/**
 * 遍历树形结构
 */
export function eachTree<T extends Record<string, any>>(
  tree: T[],
  callback: (node: T, index: number, parent: T | null) => void,
  childrenField: string = 'children'
): void {
  if (!Array.isArray(tree)) return;
  const traverse = (nodes: T[], parent: T | null = null) => {
    nodes.forEach((node, index) => {
      callback(node, index, parent);
      const children = node[childrenField];
      if (Array.isArray(children) && children.length > 0) {
        traverse(children, node);
      }
    });
  };
  traverse(tree);
}

/**
 * 菜单转路由
 */
export function menuToRoutes(
  menus: MenuItem[] = [],
  getComponent?: (component: string, menu?: MenuItem, name?: string) => any,
  added?: RouteRecordRaw[]
): RouteRecordRaw[] {
  if (!Array.isArray(menus) || menus.length === 0) return [];
  // 创建已添加路由映射
  const addedMap = new Map<string, boolean>();
  if (Array.isArray(added)) {
    added.forEach(route => {
      if (route?.path) addedMap.set(route.path, true);
    });
  }
  const routes: RouteRecordRaw[] = [];
  // 递归处理菜单
  const processMenu = (menu: MenuItem, parentPath: string = ''): RouteRecordRaw | null => {
    // 安全检查
    if (!menu || typeof menu !== 'object') return null;
    // 隐藏菜单跳过
    if (menu.meta?.hidden) return null;
    // 构建完整路径
    let fullPath = menu.path || '';
    if (!fullPath) return null;
    if (parentPath) {
      fullPath = parentPath.endsWith('/')
        ? `${parentPath}${menu.path}`
        : `${parentPath}/${menu.path}`;
    }
    fullPath = fullPath.replace(/\/+/g, '/');
    // 检查是否已存在
    if (addedMap.has(fullPath)) return null;
    // 处理外链
    if (menu.meta?.external || menu.meta?.externalUrl) {
      return {
        path: fullPath,
        name: menu.name || `External_${fullPath.replace(/\//g, '_')}`,
        redirect: menu.meta?.externalUrl,
        meta: {
          ...menu.meta,
          title: menu.meta?.title || menu.name,
          isExternal: true
        }
      };
    }
    // 获取组件
    let component = null;
    if (menu.component && getComponent) component = getComponent(menu.component, menu, menu.name);
    // 创建路由对象
    const route: RouteRecordRaw = {
      path: fullPath,
      name: menu.name || `Route_${fullPath.replace(/\//g, '_')}`,
      component: component,
      redirect: menu.redirect,
      meta: {
        ...menu.meta,
        title: menu.meta?.title || menu.name
      }
    };
    // 处理子菜单
    if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
      const children: RouteRecordRaw[] = [];
      menu.children.forEach(child => {
        const childRoute = processMenu(child, fullPath);
        if (childRoute) children.push(childRoute);
      });
      if (children.length > 0) route.children = children;
      // 如果没有有效的子路由，但当前路由也没有组件，则返回 null
      else {
        if (!component && !route.redirect) return null;
      }
    }
    return route;
  };
  // 处理所有顶级菜单
  menus.forEach(menu => {
    try {
      const route = processMenu(menu);
      if (route) routes.push(route);
    } catch (error) {
      console.error('处理菜单出错:', menu, error);
    }
  });
  return routes;
}

/**
 * 根据当前路径获取面包屑项目
 * @param menus 平铺菜单
 * @param currentPath 当前路径
 */
export function getBreadcrumbItems(
  menus: FlatMenuItem[],
  currentPath: string
): BreadcrumbItem[] {
  const result: BreadcrumbItem[] = []
  // 获取相对路径
  const relativePath = currentPath.replace('/layout/', '')
  const pathSegments = relativePath.split('/').filter(Boolean)
  // 构建每一级的完整路径并查找对应的菜单
  let currentBuildPath = ''
  for (let i = 0; i < pathSegments.length; i++) {
    currentBuildPath = currentBuildPath
      ? `${currentBuildPath}/${pathSegments[i]}`
      : pathSegments[i]
    // 查找当前路径对应的菜单
    const menu = menus.find(m => m.fullPath === currentBuildPath)
    if (menu) {
      // 找到菜单，使用 meta.title
      result.push({
        title: menu.meta?.title || menu.name || pathSegments[i],
        icon: menu.meta?.icon,
        path: `/layout/${currentBuildPath}`,
        key: menu.name || currentBuildPath,
        disabled: menu.meta?.breadcrumb === false
      })
    } else {
      // 没找到菜单，使用路径片段
      result.push({
        title: pathSegments[i],
        path: `/layout/${currentBuildPath}`,
        key: currentBuildPath,
        disabled: true
      })
    }
  }
  return result
}


/**
 * 扁平化菜单
 */
export function flattenMenus (
  menus: MenuItem[],
  parentPath: string = ''
): FlatMenuItem[] {
  let result: FlatMenuItem[] = [];
  menus.forEach(menu => {
    if (menu.meta?.hidden) return;
    const fullPath = parentPath? `${parentPath}/${menu.path}`.replace(/\/+/g, '/')
      : menu.path;
    result.push({
      fullPath:fullPath,
      meta:menu.meta || "undefined",
      parentPath: parentPath || undefined
    });
    if (menu.children && menu.children.length > 0) {
      result = result.concat(flattenMenus(menu.children, fullPath));
    }
  });
  return result;
};
